import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { IoService } from '../../../services/io/io.service';
import { LocationService } from '../../../services/location/location.service';
import { ReportService } from '../../../services/report/report.service';

// A dashboard KPI tile (element.type === 'widget'). Loads its own figures
// from POST /widget/loadWidgets for the header's active location, refreshes
// on the widget's updateFrequency while shown, and opens the backing report
// when clicked (not while the dashboard layout is being edited — the tile is
// then a plain drag handle).
//
// Payload shape (see CloudAPI modules/widget/widget-c.ts):
//   { key, name, type: 'kpi' | 'kpiTable', reportKey, updateFrequency,
//     metrics: [{ path, name, type }],
//     windows: [{ key, label, values: { [path]: number }, rows?: [{ locationId, locationName, values }] }],
//     trend?: [{ dayKey, label, value }],   // primary metric per day, oldest first
//     error? }
@Component({
  selector: 'app-dashboard-widget',
  templateUrl: './dashboard-widget.component.html',
  styleUrls: ['./dashboard-widget.component.scss']
})
export class DashboardWidgetComponent implements OnInit, OnDestroy {
  @Input() element: any;
  @Input() editing = false;

  widget: any = null;
  loading = true;
  error: string = null;

  // Sparkline geometry (viewBox units).
  readonly sparkWidth = 120;
  readonly sparkHeight = 28;

  private timer: any = null;
  private locationSubscription: Subscription;
  private destroyed = false;

  constructor(
    private ioService: IoService,
    private locationService: LocationService,
    private reportService: ReportService,
  ) { }

  ngOnInit(): void {
    this.load();
    this.locationSubscription = this.locationService.activeLocationSetObservable().subscribe(() => this.load());
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearTimer();
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
    }
  }

  get primaryMetric(): any {
    return this.widget && this.widget.metrics && this.widget.metrics[0];
  }

  get secondaryMetrics(): any[] {
    return this.widget && this.widget.metrics ? this.widget.metrics.slice(1) : [];
  }

  get today(): any {
    return this.windowByKey('today');
  }

  get yesterday(): any {
    return this.windowByKey('yesterday');
  }

  get monthToDate(): any {
    return this.windowByKey('monthToDate');
  }

  windowByKey(key: string): any {
    return this.widget && Array.isArray(this.widget.windows)
      ? this.widget.windows.find(w => w.key === key) || null
      : null;
  }

  // A location with no activity in a window has no row: that reads as 0.
  value(win: any, metric: any, row: any = null): any {
    const values = row ? row.values : (win ? win.values : null);
    const v = values && metric ? values[metric.path] : null;
    return v == null ? 0 : v;
  }

  // Signed change of the primary metric, today vs yesterday. `direction` is
  // 'up' | 'down' | 'flat'; up is good for sales figures.
  get delta(): any {
    const today = this.today;
    const yesterday = this.yesterday;
    const metric = this.primaryMetric;
    if (!today || !yesterday || !metric) {
      return null;
    }
    const current = this.value(today, metric);
    const previous = this.value(yesterday, metric);
    const change = current - previous;
    const percent = previous ? (change / Math.abs(previous)) : null;
    return {
      change,
      percent,
      direction: change > 0 ? 'up' : (change < 0 ? 'down' : 'flat'),
      label: 'vs yesterday',
    };
  }

  // Trend points as an SVG polyline in the sparkline viewBox; the last point
  // (today) is also exposed for the accent marker.
  get trend(): any[] {
    return (this.widget && Array.isArray(this.widget.trend)) ? this.widget.trend : [];
  }

  get sparkPoints(): { x: number, y: number, point: any }[] {
    const trend = this.trend;
    if (trend.length < 2) {
      return [];
    }
    const max = Math.max(...trend.map(t => t.value || 0), 0);
    const pad = 3;
    const usable = this.sparkHeight - pad * 2;
    return trend.map((point, i) => ({
      x: (i / (trend.length - 1)) * this.sparkWidth,
      y: max > 0 ? (this.sparkHeight - pad - ((point.value || 0) / max) * usable) : (this.sparkHeight - pad),
      point,
    }));
  }

  get sparkPath(): string {
    return this.sparkPoints.map(p => p.x.toFixed(1) + ',' + p.y.toFixed(1)).join(' ');
  }

  get sparkLast(): { x: number, y: number, point: any } {
    const points = this.sparkPoints;
    return points.length ? points[points.length - 1] : null;
  }

  get sparkTitle(): string {
    const trend = this.trend;
    if (!trend.length) {
      return '';
    }
    return trend.length + '-day trend, ' + trend[0].label + ' to ' + trend[trend.length - 1].label;
  }

  // Rows for the table type: the union of locations seen in either window (a
  // store with no sales today still has its month-to-date line), with each
  // location's share of the month-to-date primary metric for the inline bar.
  get tableRows(): any[] {
    const today = this.today;
    const mtd = this.monthToDate;
    const metric = this.primaryMetric;
    const byId: any = {};
    for (const win of [today, mtd]) {
      for (const row of ((win && win.rows) || [])) {
        const id = String(row.locationId);
        if (!byId[id]) {
          byId[id] = { locationId: row.locationId, locationName: row.locationName };
        }
      }
    }
    const rows = Object.values(byId)
      .sort((a: any, b: any) => String(a.locationName).localeCompare(String(b.locationName)))
      .map((row: any) => ({
        locationName: row.locationName,
        today: today ? (today.rows || []).find(r => String(r.locationId) === String(row.locationId)) : null,
        monthToDate: mtd ? (mtd.rows || []).find(r => String(r.locationId) === String(row.locationId)) : null,
        share: 0,
      }));
    const total = rows.reduce((sum, row) => sum + Math.max(0, this.value(null, metric, row.monthToDate)), 0);
    for (const row of rows) {
      row.share = total > 0 ? Math.max(0, this.value(null, metric, row.monthToDate)) / total : 0;
    }
    return rows;
  }

  load = async () => {
    this.clearTimer();
    try {
      const response: any = await this.ioService.post('/widget/loadWidgets', {
        widgetKeys: [this.element.key],
        locationId: this.locationService.getActiveLocationId() || null,
        utcOffset: new Date().getTimezoneOffset(),
      });
      if (this.destroyed) { return; }
      const widget = Array.isArray(response) ? response.find(w => w.key === this.element.key) : null;
      this.widget = widget || null;
      this.error = widget ? (widget.error || null) : 'Not available.';
    } catch (e) {
      if (this.destroyed) { return; }
      this.error = (e && e.error && e.error.msg) || 'Could not load.';
    }
    this.loading = false;
    const seconds = (this.widget && this.widget.updateFrequency) || this.element.updateFrequency || 300;
    this.timer = setTimeout(() => this.load(), Math.max(30, seconds) * 1000);
  }

  private clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  open(event: Event) {
    if (this.editing) {
      return;
    }
    event.stopPropagation();
    const reportKey = (this.widget && this.widget.reportKey) || this.element.reportKey;
    if (reportKey) {
      this.reportService.navigateToReport(reportKey);
    }
  }
}
